import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import { KeywordsOrder, OrderType } from "./types";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { TimeProgressBar } from "./components/TimeProgressBar";
import { formatPrice } from "./utils/formatters";
import KeywordSection from "./components/KeywordSection";
import Spinner from "../../Spinner";
import restartIcon from "../../../components/SVGcomponents/restart-icon.svg";
import { getStoreIcon } from "..";

dayjs.extend(isSameOrBefore);

interface KeywordOrderDetailsProps {
  order: KeywordsOrder;
  loading: boolean;
}

interface Visit {
  date: string;
  visits: number;
  total: number;
  target?: number;
  time?: string;
  timeRange?: string | number;
  startTime?: string;
  endTime?: string;
  price: number;
  progress: number;
  totalUsed: number;
  title: string;
}

interface DateInstall {
  date: string;
  installs: number;
  total: number;
  target?: number;
  time?: string;
  timeRange?: string | number;
  startTime?: string;
  endTime?: string;
  price: number;
}

interface KeywordData {
  keyword: string;
  visits: Visit[];
  installs: DateInstall[];
  totalVisits: {
    current: number;
    total: number;
    price: number;
  };
  totalInstalls: {
    current: number;
    total: number;
    price: number;
  };
}

const KeywordOrderDetails: React.FC<KeywordOrderDetailsProps> = ({
  order,
  loading,
}) => {
  const [processedData, setProcessedData] = useState<any>(null);

  const createFallbackData = (orderInfo: KeywordsOrder): any => {
    try {
      return {
        initialDate: orderInfo?.initialDate || new Date().toISOString(),
        endDate: orderInfo?.endDate || new Date().toISOString(),
        app: {
          store: orderInfo?.app?.store || "",
          country: orderInfo?.app?.country || "",
        },
        orderPrice: orderInfo?.orderPrice || 0,
        keywords:
          orderInfo?.keywords?.map((k) => ({
            ...k,
            keyword: orderInfo?.orderType === "direct" ? "Direct" : k.keyword,
          })) || [],
      };
    } catch (error) {
      console.error("Error creating fallback data:", error);
      return {
        initialDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        app: { store: "", country: "" },
        orderPrice: 0,
        keywords: [],
      };
    }
  };

  const safelyPreprocessServerData = (serverData: any) => {
    try {
      if (!serverData) return {};

      const data = { ...serverData };

      // For direct orders, create a default structure if data is missing
      if (order.orderType === "direct") {
        const defaultDirectData = {
          initialDate: data.initialDate || order.initialDate,
          endDate: data.endDate || order.endDate,
          app: {
            store: data.app?.store || order.app?.store,
            country: data.app?.country || order.app?.country,
          },
          orderPrice: data.orderPrice || order.orderPrice,
          keywords: [
            {
              keyword: "Direct",
              visits: [] as Visit[],
              installs: [] as DateInstall[],
              totalVisits: { current: 0, total: 0, price: 0 },
              totalInstalls: { current: 0, total: 0, price: 0 },
            },
          ],
        };

        // If we have actual data from the server, merge it with our default structure
        if (data.keywords && data.keywords[0]) {
          defaultDirectData.keywords[0] = {
            ...defaultDirectData.keywords[0],
            ...data.keywords[0],
            keyword: "Direct", // Always ensure keyword is 'Direct'
          };
        }

        return defaultDirectData;
      }

      // Handle regular keyword orders
      if (data.keywords && Array.isArray(data.keywords)) {
        data.keywords = data.keywords
          .map((keyword: any) => {
            if (!keyword) return null;

            const safeKeyword = { ...keyword };

            if (!Array.isArray(safeKeyword.visits)) {
              safeKeyword.visits = [];
            } else {
              safeKeyword.visits = safeKeyword.visits
                .filter(Boolean)
                .map((visit: any) => {
                  const startTime = visit?.startTime || "00:00";
                  const endTime = visit?.endTime || "21:00";
                  const formattedTimeRange = `${startTime} - ${endTime}`;
                  const visitObj = {
                    ...visit,
                    title: "Visits",
                    date: visit?.date || "",
                    visits: Number(visit?.visits || 0),
                    total: Number(visit?.total || 0),
                    timeRange: visit?.timeRange || formattedTimeRange,
                    startTime: startTime,
                    endTime: endTime,
                    price: Number(visit?.price || 0),
                    progress: 0,
                    totalUsed: visit.visits || 0,
                  };
                  return visitObj;
                });
            }

            if (!Array.isArray(safeKeyword.installs)) {
              safeKeyword.installs = [];
            } else {
              safeKeyword.installs = safeKeyword.installs
                .filter(Boolean)
                .map((install: any) => {
                  const startTime = install?.startTime || "00:00";
                  const endTime = install?.endTime || "21:00";
                  const formattedTimeRange = `${startTime} - ${endTime}`;
                  const installObj = {
                    ...install,
                    title: "Installs",
                    date: install?.date || "",
                    installs: Number(install?.installs || 0),
                    total: Number(install?.total || 0),
                    timeRange: install?.timeRange || formattedTimeRange,
                    startTime: startTime,
                    endTime: endTime,
                    price: Number(install?.price || 0),
                    totalUsed: install.installs || 0,
                  };
                  return installObj;
                });
            }

            // Убедимся, что totalVisits и totalInstalls корректно инициализированы
            if (!safeKeyword.totalVisits) {
              safeKeyword.totalVisits = { current: 0, target: 0, price: 0 };
            } else {
              safeKeyword.totalVisits = {
                current: Number(safeKeyword.totalVisits.current || 0),
                target: Number(safeKeyword.totalVisits.target || 0),
                price: Number(safeKeyword.totalVisits.price || 0),
              };
            }

            if (!safeKeyword.totalInstalls) {
              safeKeyword.totalInstalls = { current: 0, target: 0, price: 0 };
            } else {
              safeKeyword.totalInstalls = {
                current: Number(safeKeyword.totalInstalls.current || 0),
                target: Number(safeKeyword.totalInstalls.target || 0),
                price: Number(safeKeyword.totalInstalls.price || 0),
              };
            }

            return safeKeyword;
          })
          .filter(Boolean);
      }

      return data;
    } catch (error) {
      console.error("Error preprocessing server data:", error);
      return {
        initialDate: order.initialDate,
        endDate: order.endDate,
        app: {
          store: order.app?.store || "",
          country: order.app?.country || "",
        },
        orderPrice: order.orderPrice,
        keywords:
          order.orderType === "direct"
            ? [
                {
                  keyword: "Direct",
                  visits: [],
                  installs: [],
                  totalVisits: { current: 0, total: 0, price: 0 },
                  totalInstalls: { current: 0, total: 0, price: 0 },
                },
              ]
            : [],
      };
    }
  };

  useEffect(() => {
    const preprocessedData = safelyPreprocessServerData(order);
    setProcessedData(preprocessedData);
  }, [order.orderId]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={`${styles.modalContent} ${styles.keywordModalContent}`}>
      <div className={styles.header}>
        <div className={styles.appInfo}>
          <div className={styles.metadata}>
            <div className={styles.platform}>
              <span>Platform</span>
              {getStoreIcon(processedData?.app?.store || "")}
            </div>
            <div className={styles.country}>
              <span>Country</span>
              <span>{processedData?.app?.country || ""}</span>
            </div>
            <TimeProgressBar
              initialDate={processedData?.initialDate || ""}
              endDate={processedData?.endDate || ""}
              status={order?.status || ""}
            />
            <div className={styles.budget}>
              <span>Budget</span>
              <span>{formatPrice(processedData?.orderPrice || 0)}</span>
            </div>
          </div>
        </div>
        <div className={styles.metadataBottom}>
          <span className={styles.metadataBottom__title}>Progress</span>
          <span className={styles.metadataBottom__line}></span>
          <button className={styles.metadataBottom__restart}>
            <img
              src={restartIcon}
              alt="restart"
              className={styles.metadataBottom__images}
            />
            <span className={styles.metadataBottom__restart__text}>
              Restart
            </span>
          </button>
        </div>
      </div>

      <div className={`${styles.content} ${styles.keywordsScroll}`}>
        {Array.isArray(processedData?.keywords) ? (
          processedData.keywords.length > 0 ? (
            processedData.keywords.map(
              (keyword: KeywordData, index: number) => {
                if (!keyword) {
                  return null;
                }

                const safeKeyword = {
                  keyword: keyword.keyword || "Неизвестное ключевое слово",
                  visits: keyword.visits || [],
                  installs: keyword.installs || [],
                  totalVisits: keyword.totalVisits || {
                    current: 0,
                    total: 0,
                    price: 0,
                  },
                  totalInstalls: keyword.totalInstalls || {
                    current: 0,
                    total: 0,
                    price: 0,
                  },
                };

                return (
                  <KeywordSection
                    key={safeKeyword.keyword + index}
                    keyword={safeKeyword}
                    isLast={index === (processedData.keywords?.length || 0) - 1}
                    store={processedData?.app?.store || ""}
                    orderPrice={Number(processedData?.orderPrice || 0)}
                    keywordsCount={processedData.keywords?.length || 0}
                    orderStatus={order?.status || ""}
                  />
                );
              },
            )
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>В заказе не найдены ключевые слова.</p>
              <p>Если это ошибка, попробуйте обновить страницу.</p>
            </div>
          )
        ) : (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>В заказе не найдены ключевые слова.</p>
            <p>Если это ошибка, попробуйте обновить страницу.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeywordOrderDetails;
